import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import { ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTheme } from './theme-provider';

const PWAInstallGuide = ({ isOpen, onClose, platform }) => {
  const { theme } = useTheme();
  const [guide, setGuide] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);

  // Map platform to guide key
  const getGuideKey = (platform) => {
    switch (platform) {
      case 'ios-safari':
        return 'pwa-install-ios-safari';
      case 'android-chrome':
        return 'pwa-install-android-chrome';
      case 'desktop-chrome':
      case 'desktop-edge':
        return 'pwa-install-desktop';
      default:
        return 'pwa-install-desktop';
    }
  };

  useEffect(() => {
    if (isOpen && platform) {      const loadGuide = async () => {
        try {
          setLoading(true);
          // Add cache-busting parameter to force fresh load
          const cacheBreaker = Date.now();
          const response = await fetch(`/guides/pwa-install-guides.json?v=${cacheBreaker}`);
          const guides = await response.json();
          const guideKey = getGuideKey(platform);
          setGuide(guides[guideKey] || null);
          setCurrentStep(0);
        } catch (error) {
          console.error('Failed to load PWA install guide:', error);
          setGuide(null);
        } finally {
          setLoading(false);
        }
      };

      loadGuide();
    }
  }, [isOpen, platform]);

  const handleNext = () => {
    if (guide && currentStep < guide.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex) => {
    setCurrentStep(stepIndex);
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!guide) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Installation Guide</DialogTitle>
            <DialogDescription>
              Sorry, we couldn't load the installation guide for your device.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end pt-4">
            <Button onClick={onClose}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const currentStepData = guide.steps[currentStep];
  const isLastStep = currentStep === guide.steps.length - 1;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-lg font-semibold">
            {guide.title}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {guide.description}
          </DialogDescription>
        </DialogHeader>

        {/* Step Progress Indicators */}
        <div className="flex justify-center space-x-2 pb-4">
          {guide.steps.map((_, index) => (
            <button
              key={index}
              onClick={() => handleStepClick(index)}
              className={cn(
                "w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-medium transition-colors",
                index === currentStep
                  ? "bg-purple-600 border-purple-600 text-white"
                  : index < currentStep
                  ? "bg-green-500 border-green-500 text-white"
                  : theme === 'light'
                  ? "border-gray-300 text-gray-400 hover:border-purple-300"
                  : "border-gray-600 text-gray-500 hover:border-purple-500"
              )}
            >
              {index < currentStep ? (
                <Check className="h-4 w-4" />
              ) : (
                index + 1
              )}
            </button>
          ))}
        </div>

        {/* Current Step Content */}
        <div className="flex-1 overflow-y-auto pb-4">
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-medium mb-2">
                Step {currentStep + 1}: {currentStepData.title}
              </h3>
              <p className="text-sm app-text-secondary mb-3">
                {currentStepData.description}
              </p>
              {currentStepData.details && (
                <p className="text-xs app-text-secondary bg-muted p-3 rounded-lg">
                  💡 {currentStepData.details}
                </p>
              )}
            </div>

            {/* Future: Video or image would go here */}
            {currentStepData.video && (
              <div className="bg-muted rounded-lg p-4 text-center text-sm app-text-secondary">
                📹 Video guide available (coming soon)
              </div>
            )}
            
            {currentStepData.image && (
              <div className="bg-muted rounded-lg p-4 text-center text-sm app-text-secondary">
                🖼️ Visual guide available (coming soon)
              </div>
            )}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center pt-4 border-t">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="flex items-center space-x-1"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Previous</span>
          </Button>

          <span className="text-sm app-text-secondary">
            {currentStep + 1} of {guide.steps.length}
          </span>

          {isLastStep ? (
            <Button
              onClick={onClose}
              className="bg-green-600 hover:bg-green-700 text-white flex items-center space-x-1"
            >
              <Check className="h-4 w-4" />
              <span>Done</span>
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="bg-purple-600 hover:bg-purple-700 text-white flex items-center space-x-1"
            >
              <span>Next</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PWAInstallGuide;